/**
 * AUTOGENERATED FILE. DO NOT EDIT.
 *
 * This file is generated by running yarn i18n:build, do not edit it manually.
 *
 * If you wish to update it, please run yarn i18n:build.
 */
/* eslint-disable no-restricted-imports */
import {i18n} from "@lingui/core";
import * as React from "react";

import {RequestInfo} from "@khan/shared-core";

// We statically import the English strings as we use them as a fallback
// for locales that don't have any translations. Additionally, it improves
// the performance of the English site by not having to load the strings at
// runtime.
// NOTE(john): This is replaced with the actual English strings in the build
// process.
const enStrings = {"ed9pvg":["Download Khan Kids from the Apple App Store"],"xGDw1O":["Download Khan Kids from Google Play"],"zLaxZZ":["Download Khan Kids from Amazon"],"hHBN6Z":["Download the app!"],"wZ1+TP":["Read Our Privacy Policy >"],"n49Csc":["khan kids logo"],"AGgRlY":["Inspire a lifetime of learning and discovery with our free, fun educational program for children ages two to eight."],"R9u9Vw":["Plays a YouTube video about Khan Academy Kids"],"APE/hy":["Khan Kids Characters Celebrating"],"GGPdfn":["Whimsical characters"],"IhFLDC":["Learning stays fun as five whimsical characters guide kids through activities and stories."],"MRueIX":["Reya Red Panda"],"X45SnD":["Original content"],"DHB9jw":["Original interactive activities, books, animated videos, and creative lessons captivate children’s attention."],"qMA8ON":["Book"],"Mf7ty/":["Diverse learning methods"],"RmbTQ5":["Young learners engage with topics through open-ended creative tasks as well as assessed activities."],"OrEDaM":["Crayon"],"BVvAYc":["Fun rewards"],"oDNBwp":["As kids learn they collect accessories like bugs, hats, and toys for their favorite characters in the app."],"NrHNXs":["Airplane"],"n2/7iT":["super simple logo"],"ySR0b7":["nhsa logo"],"4Hak6E":["bellweather media logo"],"hjFl57":["young explorer logo"],"GXipvd":["It's personalized"],"lQ7eAY":["Our personalized learning path dynamically adapts, ensuring each child is presented with activities, books, educational videos, and creative lessons that allow them to learn at their own pace, creating an individualized experience for every learner."],"CFjiR7":["Illustration of a tablet showing the Khan Academy Kids home screen"],"fioxC3":["Independent Learning"],"kaZHKz":["Kids can learn independently in the the Khan Academy Kids Library—a curated collection of activities, books, videos, and coloring pages. Our book reader allows students to follow along with recorded audio narration or read on their own across our fiction, non-fiction and fiction leveled books."],"lIxEzT":["Illustration of a tablet showing the Khan Academy Kids library screen"],"3TV+xb":["Holistic Approach"],"PTTFlW":["A holistic approach engages students in core subjects like early literacy, language, and math while encouraging creativity and building social-emotional skills."],"gA54zw":["Illustration of a tablet showing Kodi and friends looking out with the text, 'Look! We see it! Come on, let's go!'. Two callouts sit on top of the tablet with the headings, 'Sharing' and 'Helping'."],"A4LbB9":["Robust Curriculum"],"NxONGH":["Thousands of activities for kids ages two to eight. Aligned with the Head Start Early Learning Outcomes Framework and Common Core Standards delve deep into topics ranging from math to motor development."],"eFNWp2":["Illustration of two tablets, each showing examples of exercises from inside the Khan Academy Kids app."],"9hXLz9":["Kids will love to learn."],"FJHxxQ":["Unique for every learner."]};

let results: Record<string, string | string[]> | undefined = undefined;
let promise: Promise<void> | null = null;

// We keep track of locales that are missing translations so we can load the
// English strings for those locales.
// NOTE(john): This array is replaced with the list of locales.
const missingTranslations: string[] = ["ur","zh-hant","sk","sgn-us","is","en-x-rtl","zu","xh","th","sw","fa","ms","he","fa-af","ar","sq"];

// We keep track of locales that have been bundled together in the same file
// so we can load them all at once.
const extraLocales: string[] = ["te","pl","ka","bn","ta","mn","ja","fv"];

/**
 * Load the i18n strings for the current locale and add them to the Lingui i18n
 * instance. This uses Suspsense so you'll want to make sure that your
 * component is wrapped in a Suspense (for loading) and an ErrorBoundary
 * (for errors).
 *
 * This function is called by the LoadStrings component, which is a Loadable
 * component that loads the strings for a route and all descendant imports.
 *
 * You can use this directly or you can use the withStrings HoC to load
 * strings for a component.
 */
export const useStrings = () => {
    if (results) {
        return results;
    }

    const locale = RequestInfo.kaLocale;

    // If the locale is English, or doesn't have any translations, we don't need to load
    // any additional strings.
    if (locale === "en" || missingTranslations.includes(locale)) {
        // Load the English strings into Lingui. This serves as a fallback for
        // locales that don't have any translations (and the actual strings for the English
        // locale).
        results = enStrings;
        i18n.load(locale, results);
        return results;
    }

    if (!promise) {
        promise = (async () => {
            const locale = RequestInfo.kaLocale;
            const localeFile = extraLocales.includes(locale) ? "extra" : locale;

            if (process.env.NODE_ENV === "production") {
                const result = (await import(`./lingui/${localeFile}.json`))
                    .default;
                results = localeFile === "extra" ? result[locale] : result;
            } else {
                results = {};
            }

            // Load the strings into Lingui
            i18n.load(locale, {...enStrings, ...results});

            promise = null;
        })();
    }

    throw promise;
};

/**
 * The HOC to load i18n strings for a route and all descendant imports.
 *
 * Examples:
 *
 * - export default withStrings(WrappedComponent);
 *
 * - export default compose(
 *      withStrings,
 *      withRouter
 *   )(WrappedComponent);
 */
export const withStrings = <C extends object>(
    WrappedComponent: React.ComponentType<C>,
) => {
    if (process.env.NODE_ENV === "test") {
        return WrappedComponent;
    }

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component.
    const WrapperComponent = (props: C): React.ReactNode => {
        useStrings();
        return <WrappedComponent {...(props as C)} />;
    };

    WrapperComponent.displayName = `withStrings(${displayName})`;

    return WrapperComponent;
};
